import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionLayout } from '@/components/shared/section-layout';
import { TabsContent } from '@/components/ui/tabs';
import { BillsDashboard } from './components/BillsDashboard';
import { BillsTable } from './components/BillsTable';
import { BillPaymentsTable } from './components/BillPaymentsTable';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { useVisibleTabs } from '@/hooks/useVisibleTabs';
import { NothingAvailable } from '@/components/NavigationUnavailable';
import type { NavKey } from '@/lib/navigation-catalog';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

type TabKey = 'overview' | 'bills' | 'payments';

// which tabs a role gets is the navigation matrix (nav_visibility), one element per tab
const TAB_KEYS: Record<TabKey, NavKey> = { overview: 'cost_management.bills.tab.overview', bills: 'cost_management.bills.tab.bills', payments: 'cost_management.bills.tab.payments' };
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'bills', label: 'Bills' },
  { value: 'payments', label: 'Payments' },
];

export function BillsPage() {
  // a link to one bill (?bill=<id>) lands on the Bills tab, where that bill opens
  const [searchParams] = useSearchParams();
  const visible = useVisibleTabs(TABS, TAB_KEYS);
  const [requestedTab, setActiveTab] = React.useState<TabKey>(() => (searchParams.has('bill') ? 'bills' : 'overview'));
  const activeTab = visible.activeOf(requestedTab) as TabKey | undefined;

  const getTabTitle = (tab: TabKey) => {
    switch (tab) {
      case 'overview': return 'Dashboard';
      case 'bills': return 'Bills Management';
      case 'payments': return 'Bill Payment Tracking';
      default: return 'Dashboard';
    }
  };

  if (activeTab === undefined) return <SectionLayout title="Bills"><NothingAvailable message="No section of Bills is available for your account." /></SectionLayout>;

  return (
    <SectionLayout title="Bills">
      <div className="mb-6">
        <h2 className="font-section-header mb-4">{getTabTitle(activeTab)}</h2>
      </div>
      <GenericTabBar
        tabs={visible.tabs}
        activeTab={activeTab}
        onTabChange={(value) => setActiveTab(value as TabKey)}
      >
        {visible.has('overview') && (
          <TabsContent value="overview" className="mt-6">
            <BillsDashboard />
          </TabsContent>
        )}
        {visible.has('bills') && (
          <TabsContent value="bills" className="mt-6">
            <BillsTable />
          </TabsContent>
        )}
        {visible.has('payments') && (
          <TabsContent value="payments" className="mt-6">
            <BillPaymentsTable />
          </TabsContent>
        )}
      </GenericTabBar>
    </SectionLayout>
  );
} 